import { Controller } from '@hotwired/stimulus'
import { debounce } from '../../../utils/functions'
import { SendEmailResponse, EmailConfirmResponse, Step } from './types'

const UNEXPECTED_ERROR_MESSAGE = 'Во время проверки произошла ошибка, повторите попытку позже'

export default class extends Controller {
    static values = { initialStep: String }
    static targets = [
        'registrationForm',
        'confirmationForm',
        'finalForm',
        'hiddenEmailInput',
        'confirmationCodeInput',
        'confirmEmailInput',
        'emailInput',
        'resendCountdownButton',
        'resendButton',
        'registrationButton',
        'countdown'
    ]

    declare readonly registrationFormTarget: HTMLFormElement
    declare readonly hasRegistrationFormTarget: boolean
    declare readonly confirmationFormTarget: HTMLFormElement
    declare readonly hasConfirmationFormTarget: boolean
    declare readonly finalFormTarget: HTMLFormElement
    declare readonly hasFinalFormTarget: boolean
    declare readonly hiddenEmailInputTarget: HTMLInputElement
    declare readonly hasHiddenEmailInputTarget: boolean
    declare readonly confirmationCodeInputTarget: HTMLInputElement
    declare readonly hasConfirmationCodeInputTarget: boolean
    declare readonly confirmEmailInputTarget: HTMLInputElement
    declare readonly hasConfirmEmailInputTarget: boolean
    declare readonly emailInputTarget: HTMLInputElement
    declare readonly hasEmailInputTarget: boolean
    declare readonly resendCountdownButtonTarget: HTMLButtonElement
    declare readonly hasResendCountdownButtonTarget: boolean
    declare readonly resendButtonTarget: HTMLButtonElement
    declare readonly hasResendButtonTarget: boolean
    declare readonly registrationButtonTarget: HTMLButtonElement
    declare readonly hasRegistrationButtonTarget: boolean
    declare readonly countdownTarget: HTMLElement
    declare readonly hasCountdownTarget: boolean

    private debouncedHandleCodeInput: (e: Event) => void = () => {}
    private isTimerFinished = false
    private timerId: number | null = null

    declare readonly initialStepValue: string
    declare readonly hasInitialStepValue: boolean

    connect() {
        if (this.hasRegistrationFormTarget) {
            this.registrationFormTarget.addEventListener('submit', this.submitRegistration as EventListener)
        }

        if (this.hasConfirmationFormTarget) {
            this.confirmationFormTarget.addEventListener('submit', this.submitCode as EventListener)
        }

        if (this.hasResendButtonTarget) {
            this.resendButtonTarget.addEventListener('click', this.resendCode as EventListener)
        }

        this.debouncedHandleCodeInput = debounce(this.handleCodeInput.bind(this), 100)

        if (this.hasConfirmationCodeInputTarget) {
            this.confirmationCodeInputTarget.addEventListener('input', this.debouncedHandleCodeInput as EventListener)
        }

        const step = this.resolveInitialStep()
        this.setStep(step)
    }

    disconnect() {
        if (this.hasRegistrationFormTarget) {
            this.registrationFormTarget.removeEventListener('submit', this.submitRegistration as EventListener)
        }

        if (this.hasConfirmationFormTarget) {
            this.confirmationFormTarget.removeEventListener('submit', this.submitCode as EventListener)
        }

        if (this.hasResendButtonTarget) {
            this.resendButtonTarget.removeEventListener('click', this.resendCode as EventListener)
        }

        if (this.hasConfirmationCodeInputTarget) {
            this.confirmationCodeInputTarget.removeEventListener('input', this.debouncedHandleCodeInput as EventListener)
        }

        if (this.timerId !== null) {
            clearInterval(this.timerId)
        }
    }

    private setStep(step: Step) {
        if (this.hasRegistrationFormTarget) {
            this.registrationFormTarget.classList.toggle('hidden', step !== 'registration')
        }

        if (this.hasConfirmationFormTarget) {
            this.confirmationFormTarget.classList.toggle('hidden', step !== 'verify')
        }

        if (this.hasFinalFormTarget) {
            this.finalFormTarget.classList.toggle('hidden', step !== 'finish')
        }
    }

    private resolveInitialStep(): Step {
        if (this.hasInitialStepValue) {
            const step = String(this.initialStepValue)

            if (step === 'finish' || step === 'verify' || step === 'registration') {
                return step as Step
            }
        }

        if (this.hasFinalFormTarget && !this.finalFormTarget.classList.contains('hidden')) {
            return 'finish'
        }

        if (this.hasConfirmationFormTarget && !this.confirmationFormTarget.classList.contains('hidden')) {
            return 'verify'
        }

        return 'registration'
    }

    private parseDate(dateString: string): number | null {
        if (!dateString) {
            return null
        }

        try {
            const formattedTime = dateString.replace(/\.\d+$/, '')
            const ts = new Date(formattedTime.replace(' ', 'T')).getTime()
            return Number.isNaN(ts) ? null : ts
        } catch {
            return null
        }
    }

    private setCountdownText(minutes: number, seconds: number) {
        if (!this.hasCountdownTarget) {
            return
        }

        this.countdownTarget.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    private createResendButtonView() {
        if (!this.hasResendCountdownButtonTarget || !this.hasResendButtonTarget) {
            return
        }

        if (this.isTimerFinished) {
            this.resendCountdownButtonTarget.classList.add('hidden')
            this.resendButtonTarget.classList.remove('hidden')
            this.resendButtonTarget.disabled = false
        } else {
            this.resendCountdownButtonTarget.classList.remove('hidden')
            this.resendButtonTarget.classList.add('hidden')
            this.resendButtonTarget.disabled = true
        }
    }

    private startCountdown(deadline: Date) {
        if (this.timerId !== null) {
            clearInterval(this.timerId)
        }

        this.timerId = window.setInterval(() => {
            const now = new Date()
            const diffSec = (deadline.getTime() - now.getTime()) / 1000

            if (diffSec < 0) {
                this.setCountdownText(0, 0)

                if (this.timerId !== null) {
                    clearInterval(this.timerId)
                }

                this.isTimerFinished = true
                this.createResendButtonView()
                return
            }

            this.setCountdownText(
                Math.floor((diffSec / 60) % 60),
                Math.floor(diffSec % 60)
            )
        }, 1000)
    }

    private async sendConfirmationCode(emailValue: string): Promise<SendEmailResponse> {
        const response = await fetch('/email-confirm/send', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: emailValue })
        })

        if (!response.ok) {
            let message: unknown = UNEXPECTED_ERROR_MESSAGE

            try {
                message = ((await response.json())?.error ?? UNEXPECTED_ERROR_MESSAGE)
            } catch {}

            this.showAlert(String(message))

            return { error: String(message) }
        }

        return (await response.json()) as SendEmailResponse
    }

    private async confirmEmailRequest(): Promise<EmailConfirmResponse> {
        const code = this.confirmationCodeInputTarget.value
        const email = this.confirmEmailInputTarget.value

        if (!email) {
            this.showAlert(UNEXPECTED_ERROR_MESSAGE)
            return { error: UNEXPECTED_ERROR_MESSAGE }
        }

        const response = await fetch('/email-confirm', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code: code.toString() })
        })

        if (!response.ok) {
            let message: unknown = UNEXPECTED_ERROR_MESSAGE

            try {
                message = ((await response.json())?.error ?? UNEXPECTED_ERROR_MESSAGE)
            } catch {}

            this.showAlert(String(message))

            return { error: String(message) }
        }

        return (await response.json()) as EmailConfirmResponse
    }

    submitRegistration = async (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        if (!this.hasEmailInputTarget) {
            return
        }

        const errorText = this.validateEmail(this.emailInputTarget.value)

        if (errorText) {
            return this.setFieldError(this.emailInputTarget, errorText)
        }

        const emailValue = this.emailInputTarget.value

        try {
            const result = await this.sendConfirmationCode(emailValue)
            if (result.error) {
                return
            }

            if (result.alreadyVerified) {
                if (this.hasHiddenEmailInputTarget) {
                    this.hiddenEmailInputTarget.value = emailValue
                }

                return this.setStep('finish')
            }

            this.setStep('verify')

            if (this.hasConfirmEmailInputTarget) {
                this.confirmEmailInputTarget.value = emailValue
            }

            const startMs = this.parseDate(result.resendAvailableAt || '')

            if (startMs) {
                this.createResendButtonView()
                this.isTimerFinished = false
                this.startCountdown(new Date(startMs))
            }

            this.handleCodeInput()
        } catch {
            this.showAlert(UNEXPECTED_ERROR_MESSAGE)
        }
    }

    submitCode = async (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        const codeError = this.validateCode(this.confirmationCodeInputTarget.value)

        if (codeError) {
            this.setFieldError(this.confirmationCodeInputTarget, codeError)
            return
        }

        try {
            const result = await this.confirmEmailRequest()

            if (!result.error) {
                if (this.hasHiddenEmailInputTarget && this.hasEmailInputTarget) {
                    this.hiddenEmailInputTarget.value = this.emailInputTarget.value
                }

                return this.setStep('finish')
            }
        } catch {
            this.showAlert(UNEXPECTED_ERROR_MESSAGE)
        }
    }

    resendCode = async (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        if (!this.hasConfirmEmailInputTarget) {
            this.showAlert(UNEXPECTED_ERROR_MESSAGE)
            return
        }

        const email = this.confirmEmailInputTarget.value

        if (!email) {
            this.showAlert(UNEXPECTED_ERROR_MESSAGE)
            return
        }

        try {
            const result = await this.sendConfirmationCode(email)

            if (result.error) {
                return
            }

            this.setStep('verify')

            const startMs = this.parseDate(result.resendAvailableAt || '')

            if (startMs) {
                this.isTimerFinished = false
                this.createResendButtonView()
                this.startCountdown(new Date(startMs))
            }

            this.handleCodeInput()
        } catch {
            this.showAlert(UNEXPECTED_ERROR_MESSAGE)
        }
    }

    handleCodeInput() {
        if (!this.hasRegistrationButtonTarget || !this.hasConfirmationCodeInputTarget) {
            return
        }

        const value = this.confirmationCodeInputTarget.value

        this.registrationButtonTarget.disabled = !!this.validateCode(value)
    }

    private setFieldError(input: HTMLInputElement, message: string) {
        input.dispatchEvent(new CustomEvent('input:add-error', { bubbles: true, detail: { message } }))
    }

    private showAlert(message: string, type: 'error' | 'success' = 'error', timeout = 10000) {
        window.dispatchEvent(new CustomEvent('alert:show', { detail: { message, type, timeout } }))
    }

    private validateEmail(value: string): string | null {
        if (!value) {
            return 'Заполните поле'
        }

        const patternt = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

        if (!patternt.test(value)) {
            return 'Введите корректный email'
        }

        return null
    }

    private validateCode(value: string): string | null {
        if (!value) {
            return 'Заполните поле'
        }

        if (!/^\d+$/.test(value)) {
            return 'Введите числовое значение'
        }

        if (value.length !== 6) {
            return 'Не корректное кодовое значение'
        }

        return null
    }
}
